<template>
  <portlet
      :actionsBlock="false"
      :icon="'fal fa-users'"
      :title="$t('menu.teams')"
  >
    <template slot="portletBody">
      <ve-table
          v-if="getTeamsWithSchedule.length"
          :max-height="400"
          height="400"
          width="100"
          :fixed-header="true"
          :expand-option="null"
          row-key-field-name="rowKey"
          :columns="getColumns"
          :cellSelectionOption="cellSelectionOption"
          :row-style-option="rowStyleOption"
          :event-custom-option="customEventOptions"
          :table-data="getTeamsWithSchedule"/>
      <div v-else>{{ $t('teams.table.noRecords') }}</div>
    </template>
  </portlet>
</template>

<script>
import {mapGetters} from "vuex";
import {customEventOptions} from "@/mixins/vueEasyTable/bodyCellEvents";
import {teamFunctions} from "@/mixins/teams/teamsFunctions";

export default {
  name: "TeamsOverview",
  mixins: [customEventOptions, teamFunctions],
  data() {
    return {
      cellSelectionOption: {
        enable: false,
      },
      virtualScrollOption: {
        enable: true,
      },
      rowStyleOption: {
        stripe: true,
        clickHighlight: true,
        hoverHighlight: true,
      }
    }
  },
  computed: {
    ...mapGetters('teams', ['getTeams']),
    ...mapGetters('schedules', ['getSchedules']),
    ...mapGetters('workflows', ['getWorkflow']),
    getTeamsWithSchedule() {
      return _.map(_.filter(this.getTeams, team => {  return  !team.fakeTeam}), team => {
        const activeSchedule = _.find(this.getSchedules, schedule => {
          return schedule.team === team.id && !schedule.workflowId && !schedule.workflowIds && moment(schedule.fromDate).isSameOrBefore(moment()) && (!schedule.deactivatedFrom || moment(schedule.deactivatedFrom).isAfter(moment()));
        });

        team.activeSchedule = activeSchedule ? activeSchedule : null;

        const nextSchedule = _.find(this.getSchedules, schedule => {
          return schedule.team === team.id &&  !schedule.workflowId && !schedule.workflowIds  && moment(schedule.fromDate).isAfter(moment(), 'milliseconds');
        });
        team.nextSchedule = nextSchedule ? nextSchedule : null;
        return team;
      })
    },
    getColumns() {
      const self = this;
      return [
        {
          field: "name",
          key: '1',
          title: this.$t('teams.name'),
          align: "left",
          renderBodyCell: ({row, column, rowIndex}, h) => {
            return <div class="team-name-cell">
              <div class="team-name">{row.name}</div>
            </div>;
          }
        }, {
          field: "code",
          key: '2',
          title: this.$t('teams.code'),
          align: "left"
        }, {
          field: "workflow",
          key: '3',
          title: this.$t('teams.workflowId'),
          align: "left",
          renderBodyCell: ({row, column, rowIndex}, h) => {
            return row.workflowIds && row.workflowIds.length  ? _.map(row.workflowIds, workflowId => {
              return  this.getWorkflow(workflowId).name
            }).join(', ') : ''
          }

        }, {
          field: "schedule.name",
          key: '4',
          title: this.$t('teams.schedule'),
          align: "left",
          translations: {
            from: this.$t('teams.scheduleSelector.from'),
            to: this.$t('teams.scheduleSelector.to')
          },
          renderBodyCell: ({row, column, rowIndex}, h) => {
            let schedules = [];
            if (row.activeSchedule) {
              schedules.push({
                name: row.activeSchedule.name,
                date: row.activeSchedule.deactivatedFrom ? column.translations.to + ' ' + moment(row.activeSchedule.deactivatedFrom).format('DD-MM-YYYY') : ''
              })
            }
            if (row.nextSchedule) {
              schedules.push({
                name: row.nextSchedule.name,
                date: row.nextSchedule.fromDate ? column.translations.from + ' ' + moment(row.nextSchedule.fromDate).format('DD-MM-YYYY') : ''
              })
            }

            if (schedules.length) {
              const listPoints = schedules.map(schedule => {
                if (schedule.date) {
                  return (<li> {schedule.name  + ' (' + schedule.date + ')'} </li>)
                } else {
                  return (<li> {schedule.name} </li>)
                }
              })
              return (<ul>
                {listPoints}
              </ul>)

            } else {
              return '-'
            }

          }
        }, {
          field: "actions",
          key: '5',
          title: this.$t('teams.table.columns.actions'),
          align: "left",
          minWidth: '500px',
          functions: [
            {id: 'delete-team-button', handler: this.deleteTeam},
            {id: 'edit-team-button', handler: this.editTeam},
            {id: 'edit-schedule-button', handler: this.editOrCreateSchedule}
          ],
          renderBodyCell: ({row, column, rowIndex}, h) => {
            return (
                <div class="row">
                  <div class="col-12 col-lg-4">
                    <button tag="delete-team-button"
                            class="table-button btn btn-danger btn-block">{this.$t('ui.delete')}</button>
                  </div>
                  <div class="col-12 col-lg-4">
                    <button tag="edit-team-button"
                            class="btn btn-primary btn-block  table-button">{this.$t('ui.edit')}</button>
                  </div>
                  <div class="col-12 col-lg-4">
                    <button tag="edit-schedule-button" class="btn table-button btn-secondary btn-block">
                      {row.activeSchedule || row.nextSchedule ? this.$t('shifts.table.actions.editSchedule') : this.$t('shifts.table.actions.createSchedule')}
                    </button>
                  </div>
                </div>
            )
          }
        }
      ]
    },
  },
  methods: {
    editTeam($event) {
      this.$emit('editTeam', _.omit($event.row, 'schedule'));
    },
    async editOrCreateSchedule($event) {
      if ($event.row.activeSchedule || $event.row.nextSchedule) {
        const nvt = this.$t('teams.scheduleSelector.nvt');
        const activeSchedule = this.$t('teams.scheduleSelector.activeSchedule', {name: $event.row.activeSchedule ? $event.row.activeSchedule.name : ''});
        const nextScheduleEdit = this.$t('teams.scheduleSelector.nextScheduleEdit', {name: $event.row.nextSchedule ? $event.row.nextSchedule.name : ''});
        const nextScheduleDelete = this.$t('teams.scheduleSelector.nextScheduleDelete', {name: $event.row.nextSchedule ? $event.row.nextSchedule.name : ''});
        const nextScheduleCreate = this.$t('teams.scheduleSelector.nextScheduleCreate');
        const from = this.$t('teams.scheduleSelector.from');
        const to = this.$t('teams.scheduleSelector.to');
        const noChoice = this.$t('teams.scheduleSelector.noChoice');

        let html = `<div class="options">`;

        if ($event.row.activeSchedule) {
          html += `<input type="radio" id="active-schedule" name="schedule" value="activeSchedule">`
          html += `<label for="active-schedule">${activeSchedule} ${$event.row.activeSchedule ? ($event.row.activeSchedule.deactivatedFrom ? ('(' + to + ' ' + moment($event.row.activeSchedule.deactivatedFrom).format('DD-MM-YYYY') + ')') : '') : '(' + nvt + ')'}</label>`
        }
        if ($event.row.nextSchedule) {
          html += `<input type="radio" id="next-schedule-edit" name="schedule" value="nextScheduleEdit">`
          html += `<label for="next-schedule-edit">${nextScheduleEdit} ${('(' + from + ' ' + moment($event.row.nextSchedule.fromDate).format('DD-MM-YYYY') + ')')}</label>`
          html += `<input type="radio" id="next-schedule-delete" name="schedule" value="nextScheduleDelete">`
          html += `<label for="next-schedule-delete">${nextScheduleDelete} ${('(' + from + ' ' + moment($event.row.nextSchedule.fromDate).format('DD-MM-YYYY') + ')')}</label>`
        } else {
          html += `<input type="radio" id="next-schedule-create" name="schedule" value="nextScheduleCreate">`
          html += `<label for="next-schedule-create">${nextScheduleCreate}</label><br>`
        }
        const swal = await this.$swal({
          title: this.$t('teams.scheduleSelector.whatDoYouWantToDo'),
          html,
          confirmButtonText: this.$root.$t('ui.ok'),
          cancelButtonText: this.$root.$t('ui.cancel'),
          showCancelButton: true,
          allowOutsideClick: false,
          reverseButtons: true,
          preConfirm: () => {
            const retVal = {
              active: document.getElementById('active-schedule') ? document.getElementById('active-schedule').checked : false,
              nextEdit: document.getElementById('next-schedule-edit') ? document.getElementById('next-schedule-edit').checked : false,
              nextDelete: document.getElementById('next-schedule-delete') ? document.getElementById('next-schedule-delete').checked : false,
              nextCreate: document.getElementById('next-schedule-create') ? document.getElementById('next-schedule-create').checked : false
            };
            // Version number the same?
            if (!retVal.active && !retVal.nextEdit && !retVal.nextCreate && !retVal.nextDelete) {
              this.$swal.showValidationMessage(noChoice);
            }

            return retVal;
          },
        });
        if (swal.value && swal.value.active) {
          this.$router.push({
            name: 'editSchedule',
            params: {scheduleId: $event.row.activeSchedule.id, teamId: $event.row.id, inputsBlocked: true}
          })
        } else if (swal.value && swal.value.nextCreate) {
          this.$router.push({
            name: 'addSchedule',
            params: {teamId: $event.row.id, inputsBlocked: false, activeScheduleId: $event.row.activeSchedule.id}
          })
        } else if (swal.value && swal.value.nextEdit) {
          this.$router.push({
            name: 'editSchedule',
            params: {scheduleId: $event.row.nextSchedule.id, teamId: $event.row.id, inputsBlocked: false}
          })
        } else if (swal.value && swal.value.nextDelete) {
          console.log("$event.row.nextSchedule.id", $event.row.nextSchedule.id)
          await this.$store.dispatch('schedules/delete', $event.row.nextSchedule.id).catch(e => {
            console.error(e)
          });
        }

      } else {
        this.$router.push({name: 'addSchedule', params: {teamId: $event.row.id, inputsBlocked: false}})
      }
    },
    async deleteTeam($event) {
      const deleteSwal = await this.$swal({
        title: this.$t("teams.delete.swal.title"),
        html: this.$t("teams.delete.swal.text", {name: $event.row.name}),
        icon: 'error',
        customClass: 'm-swal-delete',
        confirmButtonText: this.$t("teams.delete.swal.confirm"),
        showCancelButton: true,
        cancelButtonText: this.$t('ui.cancel'),
        reverseButtons: true
      });
      if (deleteSwal.value) {
        await this.$store.dispatch('teams/delete', $event.row.id);
        this.$toasted.global.successRequest(this.$t("teams.delete.toast.success"))
      }
    }
  },
}
</script>

<style>
.disabled-label {
  opacity: 0.8;
}

.table-button {
  white-space: normal;
  height: 100%;
  min-width: fit-content;
}

.team-name-cell-deactivated, .team-name-cell-future {
  display: grid;
  grid-template-columns: 1fr auto;
  grid-template-rows: 1fr;
  grid-column-gap: 12px;
  grid-row-gap: 0px;
}

.team-description[future] {
  background: #e2ffed;
}

.team-description[deactivated] {
  background: #ffe0b8;
}

.team-name {
  display: grid;
  align-content: center;
}

.team-description {
  text-align: center;
  margin-top: 0.1rem;
  margin-bottom: 0.1rem;
  border-radius: 25px;
  padding-left: 1rem;
  padding-right: 1rem;
  font-weight: bold;
  font-size: 0.9em;
  display: grid;
  align-content: center;
  cursor: pointer;
}


#active-schedule, #next-schedule-edit, #next-schedule-delete, #next-schedule-create {
  margin-right: 1rem;
}

#swal2-content > .options {
  text-align: left;
  display: grid;
  grid-template-columns: auto 1fr;

  label {
    margin-bottom: 0px;
  }

}


</style>
